// generate APA standard citation format (7th edition)

import { extractTagValue, getValidContributors } from './citationHelpers.js';

// Textual first-edition words for every locale the reader ships (da, de, en,
// es, fr, it, pl, pt, sv). Matched exact or as a prefix — never anywhere in
// the string, so "décima primera edición" (11th) is not suppressed.
const FIRST_EDITION_WORDS = [
    'first',
    '1st',
    'primera',
    'primeira',
    'première',
    'premiere',
    'prima',
    'erste',
    'første',
    'första',
    'pierwsza',
    'pierwsze',
];

// APA 7: list every author up to 20; for 21+ keep the first 19, an ellipsis
// (no ampersand), and the final author.
const MAX_LISTED_AUTHORS = 20;
const AUTHORS_BEFORE_ELLIPSIS = 19;
const AUTHOR_ELLIPSIS = '. . .';

/**
 * Builds the initials for a given first name: one initial per word, and one
 * per hyphenated component ("Jean-Paul" → "J.-P.").
 * @param {string} firstName - Contributor first name (e.g., "Juan Carlos")
 * @returns {string} Formatted initials (e.g., "J. C.")
 */
function getApaInitials(firstName) {
    return firstName
        .split(/\s+/)
        .filter(Boolean)
        .map(namePart =>
            namePart
                .split('-')
                .filter(Boolean)
                .map(component => `${component.charAt(0).toUpperCase()}.`)
                .join('-')
        )
        .join(' ');
}

/**
 * Formats a contributor name to APA standard: Lastname, F. M.
 * Falls back to the available part when first or last name is missing.
 * @param {{first_name: string, last_name: string}} contributor - Contributor object
 * @returns {string} Formatted contributor name (e.g., "González Sánchez, A.")
 */
function formatApaContributorName(contributor) {
    const firstName = contributor.first_name?.trim() || '';
    const lastName = contributor.last_name?.trim() || '';

    if (!lastName) {
        return firstName;
    }

    if (!firstName) {
        return lastName;
    }

    return `${lastName}, ${getApaInitials(firstName)}`;
}

/**
 * Formats the authors list according to APA rules.
 * - 1 author: González Sánchez, A.
 * - 2 authors: Silva, J., & Santos, M.
 * - 3-20 authors: Silva, J., Santos, M., & Costa, P.
 * - 21+ authors: first 19, ellipsis, final author (no ampersand)
 * Falls back to the raw author display names when no structured contributor exists.
 * @param {Object} tags - Issue tags containing contributors array
 * @returns {string} Formatted authors string
 */
function getApaAuthors(tags) {
    const contributors = getValidContributors(tags);

    if (contributors.length === 0) {
        return Object.prototype.hasOwnProperty.call(tags, 'author') ? tags.author.join(', ') : '';
    }

    const names = contributors.map(formatApaContributorName).filter(Boolean);

    if (names.length <= 1) {
        return names.join('');
    }

    if (names.length > MAX_LISTED_AUTHORS) {
        const listed = names.slice(0, AUTHORS_BEFORE_ELLIPSIS);
        return `${listed.join(', ')}, ${AUTHOR_ELLIPSIS} ${names[names.length - 1]}`;
    }

    return `${names.slice(0, -1).join(', ')}, & ${names[names.length - 1]}`;
}

/**
 * Returns the edition to mention according to APA rules.
 * The first edition is never mentioned. Numeric editions — how most tenants
 * store them ("2", "2ª ed.", "2ª edicion") — are normalized to the canonical
 * "2.ª ed."; values with no leading number are shown as stored.
 * @param {Object} tags - Issue tags containing edition
 * @returns {string} Edition mention or empty string
 */
function getApaEdition(tags) {
    const edition = extractTagValue(tags.edition);

    if (!edition) {
        return '';
    }

    const editionNumber = parseInt(edition, 10);
    const normalizedEdition = edition.toLowerCase();
    const isFirstEditionWord = FIRST_EDITION_WORDS.some(
        word => normalizedEdition === word || normalizedEdition.startsWith(`${word} `)
    );

    if (editionNumber === 1 || isFirstEditionWord) {
        return '';
    }

    if (!Number.isNaN(editionNumber)) {
        return `${editionNumber}.ª ed.`;
    }

    return edition;
}

// APA 7 separates multiple publishers with a semicolon.
function getApaPublisher(tags) {
    return Object.prototype.hasOwnProperty.call(tags, 'publisher') ? tags.publisher.join('; ') : '';
}

function getApaPublicationDate(issue) {
    return Object.prototype.hasOwnProperty.call(issue, 'publication_date') ? `(${issue.publication_date})` : '';
}

function getApaTitle(issue, title) {
    return Object.prototype.hasOwnProperty.call(issue, 'title') && issue.title.length ? issue.title : title;
}

/**
 * Returns the public URL of the publication, without reader navigation state.
 * Prefers the storefront share URL and falls back to the plain reader URL.
 * @param {Object} issue - Issue object
 * @returns {string} Publication URL or empty string
 */
function getApaIssueUrl(issue) {
    const shareUrl = typeof issue.share_url === 'string' ? issue.share_url.trim() : '';

    if (shareUrl) {
        return shareUrl;
    }

    return typeof issue.issue_url === 'string' ? issue.issue_url.trim() : '';
}

function formatValue(string) {
    const trimmedString = string.trim();
    if (!trimmedString) {
        return '';
    }
    return trimmedString.endsWith('.') ? `${trimmedString} ` : `${trimmedString}. `;
}

const ApaCitation = (text, issue, currentLocation, issueTitle) => {
    const author = getApaAuthors(issue.tags);
    const date = getApaPublicationDate(issue);
    const title = getApaTitle(issue, issueTitle);
    const edition = getApaEdition(issue.tags);
    const publisher = getApaPublisher(issue.tags);
    const url = getApaIssueUrl(issue);

    // APA attaches the edition to the title with no period in between:
    // "Título (2.ª ed.). Editorial." A terminal period on the title is
    // dropped; "?" and "!" are kept as part of the title.
    const titleWithEdition = edition ? `${title.trim().replace(/\.$/, '')} (${edition})` : title;

    const citation = `${formatValue(author)}${formatValue(date)}${formatValue(titleWithEdition)}${formatValue(publisher)}${url}`;

    if (text && text.trim()) {
        return `"${text}"

${citation}`;
    }

    return citation;
};

export default ApaCitation;
